import { keyBy, mapValues } from 'lodash';
import AppConstants from '../../constants/appConstants';
import { invertKeysToValues } from '../../utils/array';
import { authGet, authPost, authPut, authenticatedDelete } from '../../utils/fecth-wrappers';
import {
  createUserPreferencesFailure,
  createUserPreferencesRequest,
  createUserPreferencesSuccess,
  deleteUserPreferencesFailure,
  deleteUserPreferencesRequest,
  deleteUserPreferencesSuccess,
  listUserPreferencesFailure,
  listUserPreferencesRequest,
  listUserPreferencesSuccess,
  updateUserPreferencesFailure,
  updateUserPreferencesRequest,
  updateUserPreferencesSuccess,
} from './actions';
import { enableTooltip } from '../tooltip/actions';
import { preferencesLabel } from '../../constants/preferencesLabel';
import { sendEvent } from '../../helpers/googleAnalytics';
import {
  trackDealerPreferencesSaved,
  formatPreferencesForTracking,
} from '../../helpers/segmentTracking';

const preferenceObj = invertKeysToValues(preferencesLabel);

const formatRequest = obj =>
  mapValues(
    keyBy(obj, o => preferenceObj[o.name]),
    'isActive'
  );

export const createUserPreferences = (
  userId,
  channelOfContact,
  userPreferences,
  relatedNinja,
  estimatedNotifications = 0
) => async (dispatch, getState) => {
  dispatch(createUserPreferencesRequest());
  try {
    const { data } = await authPost(`${AppConstants.API.user_preferences}`, {
      userId,
      channelOfContact,
      userPreferences,
      ninja: {
        email: relatedNinja,
      },
    });

    // Arredondar estimativa de notificações do frontend
    const roundedEstimation = Math.round(estimatedNotifications || 0);

    if (!!relatedNinja) await dispatch(enableTooltip({ messageId: 12 }));
    else if (roundedEstimation > 0) await dispatch(enableTooltip({ messageId: 4 }));
    else await dispatch(enableTooltip({ messageId: 8 }));

    const {
      id,
      channelOfContact: receivers,
      userPreferences: preferences,
      ninja,
      updated,
      updatedAt,
      updated_at,
    } = data || {};
    dispatch(
      createUserPreferencesSuccess({
        id,
        channelOfContact: receivers,
        preferences,
        ninja,
        updatedAt: updated || updatedAt || updated_at,
      })
    );
    
    // Enviar evento de tracking para o Segment
    try {
      const user = getState().authentication.user;
      const profile = getState().user?.profile?.profile;
      const formattedPrefs = formatPreferencesForTracking(preferences, receivers);
      
      trackDealerPreferencesSaved({
        dealershipId: userId,
        dealershipName: user?.dealershipName || '',
        email: user?.email || '',
        phoneNumber: profile?.phoneNumber || user?.phoneNumber || '',
        relatedNinja: relatedNinja || '',
        actionType: 'create',
        estimatedNotifications: roundedEstimation,
        ...formattedPrefs,
      });
    } catch (trackingError) {
      console.error('Erro ao enviar tracking de preferências:', trackingError);
    }
    
    dispatch(listUserPreferences(userId));
  } catch (error) {
    dispatch(createUserPreferencesFailure(error));
  }
};

export const updateUserPreferences = (
  preferenceId,
  userId,
  channelOfContact,
  userPreferences,
  relatedNinja,
  estimatedNotifications = 0
) => async (dispatch, getState) => {
  dispatch(updateUserPreferencesRequest());
  try {
    const prefs = await authPut(
      `${AppConstants.API.user_preferences}/update-by-id/${preferenceId}`,
      {
        userId,
        channelOfContact,
        userPreferences,
        ninja: {
          email: relatedNinja,
        },
      }
    );

    // Arredondar estimativa de notificações do frontend
    const roundedEstimation = Math.round(estimatedNotifications || 0);

    if (!!relatedNinja) await dispatch(enableTooltip({ messageId: 12 }));
    else if (roundedEstimation > 0) await dispatch(enableTooltip({ messageId: 5 }));
    else await dispatch(enableTooltip({ messageId: 6 }));

    const selected = Object.keys(userPreferences)
      .filter(key => userPreferences[key])
      .join(', ');
    const user = getState().authentication.user;

    sendEvent('preferencesTracker', {
      dealershipId: user?.id,
      dealershipEmail: user?.email,
      dealershipName: user.dealershipName,
      selectedCategory: selected,
      carResults: roundedEstimation,
      eventCategory: 'Auctions',
      eventAction: 'SavePreferences',
      eventLabel: 'Pedir Carros',
    });

    const {
      id,
      channelOfContact: receivers,
      userPreferences: preferences,
      ninja,
      updated,
      updatedAt,
      updated_at,
    } = prefs?.data;
    const formattedChannelOfContact = formatRequest(receivers);
    const formattedPreferences = formatRequest(preferences);
    
    dispatch(
      updateUserPreferencesSuccess({
        id,
        channelOfContact: formattedChannelOfContact,
        preferences: formattedPreferences,
        ninja,
        updatedAt: updated || updatedAt || updated_at,
      })
    );
    
    // Enviar evento de tracking para o Segment
    try {
      const profile = getState().user?.profile?.profile;
      const formattedPrefs = formatPreferencesForTracking(preferences, receivers);
      
      trackDealerPreferencesSaved({
        dealershipId: userId,
        dealershipName: user?.dealershipName || '',
        email: user?.email || '',
        phoneNumber: profile?.phoneNumber || user?.phoneNumber || '',
        relatedNinja: relatedNinja || '',
        actionType: 'update',
        estimatedNotifications: roundedEstimation,
        ...formattedPrefs,
      });
    } catch (trackingError) {
      console.error('Erro ao enviar tracking de atualização de preferências:', trackingError);
    }
  } catch (error) {
    dispatch(updateUserPreferencesFailure(error));
    dispatch(enableTooltip({ messageId: 7 }));
    console.log('error: ', error);
  }
};

export const deleteUserPreferences = preferenceId => async dispatch => {
  dispatch(deleteUserPreferencesRequest());
  try {
    await authenticatedDelete(`${AppConstants.API.user_preferences}/${preferenceId}`);
    dispatch(deleteUserPreferencesSuccess());
  } catch (error) {
    dispatch(deleteUserPreferencesFailure(error));
  }
};

export const listUserPreferences = userId => async dispatch => {
  dispatch(listUserPreferencesRequest());
  try {
    const { data } = await authGet(
      `${AppConstants.API.user_preferences}/find-all/by-user-id/${userId}`
    );
    console.log('listUserPreferences - data completo:', data);
    console.log('listUserPreferences - data[0]:', data[0]);
    console.log('listUserPreferences - data[0] keys:', data[0] ? Object.keys(data[0]) : 'no data');

    const firstItem = data[0] || {};
    const {
      id,
      channelOfContact: receivers,
      userPreferences: preferences,
      ninja,
      updated,
      updatedAt,
      updated_at,
    } = firstItem;

    const channelOfContact = formatRequest(receivers);
    // A API retorna "updated", não "updatedAt" ou "updated_at"
    const finalUpdatedAt = updated || updatedAt || updated_at;
    const payload = {
      id,
      channelOfContact,
      preferences,
      ninja,
      updatedAt: finalUpdatedAt,
    };
    console.log('listUserPreferences - updated da API:', updated);
    console.log('listUserPreferences - payload.updatedAt:', payload.updatedAt);
    dispatch(listUserPreferencesSuccess(payload));
  } catch (error) {
    dispatch(listUserPreferencesFailure(error));
  }
};
